/**
 * Map Getting Started Panel Store
 *
 * localStorage-persisted state for map-level onboarding panel.
 * Tracks collapsed/dismissed state and completed items per map.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

// =============================================================================
// Types
// =============================================================================

export type GettingStartedItem =
  | "poi_created"
  | "poi_enriched"
  | "label_added"
  | "map_published";

interface MapPanelState {
  collapsed: boolean;
  dismissed: boolean;
  completedItems: GettingStartedItem[];
}

interface MapGettingStartedState {
  // State per map (keyed by mapId)
  maps: Record<string, MapPanelState>;

  // Actions
  getMapState: (mapId: string) => MapPanelState;
  setCollapsed: (mapId: string, collapsed: boolean) => void;
  setDismissed: (mapId: string, dismissed: boolean) => void;
  markItemComplete: (mapId: string, item: GettingStartedItem) => void;
  isItemComplete: (mapId: string, item: GettingStartedItem) => boolean;
  getCompletedCount: (mapId: string) => number;
  shouldShowPanel: (mapId: string) => boolean;
  resetMap: (mapId: string) => void;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_MAP_STATE: MapPanelState = {
  collapsed: false,
  dismissed: false,
  completedItems: [],
};

const TOTAL_ITEMS = 4;

// =============================================================================
// Store
// =============================================================================

export const useMapGettingStartedStore = create<MapGettingStartedState>()(
  persist(
    (set, get) => ({
      maps: {},

      getMapState: (mapId: string): MapPanelState => {
        return get().maps[mapId] ?? DEFAULT_MAP_STATE;
      },

      setCollapsed: (mapId: string, collapsed: boolean) => {
        set((state) => ({
          maps: {
            ...state.maps,
            [mapId]: {
              ...DEFAULT_MAP_STATE,
              ...state.maps[mapId],
              collapsed,
            },
          },
        }));
      },

      setDismissed: (mapId: string, dismissed: boolean) => {
        set((state) => ({
          maps: {
            ...state.maps,
            [mapId]: {
              ...DEFAULT_MAP_STATE,
              ...state.maps[mapId],
              dismissed,
            },
          },
        }));
      },

      markItemComplete: (mapId: string, item: GettingStartedItem) => {
        set((state) => {
          const currentState = state.maps[mapId] ?? DEFAULT_MAP_STATE;
          const completedItems = currentState.completedItems.includes(item)
            ? currentState.completedItems
            : [...currentState.completedItems, item];

          return {
            maps: {
              ...state.maps,
              [mapId]: {
                ...currentState,
                completedItems,
              },
            },
          };
        });
      },

      isItemComplete: (mapId: string, item: GettingStartedItem): boolean => {
        const state = get().maps[mapId];
        return state?.completedItems.includes(item) ?? false;
      },

      getCompletedCount: (mapId: string): number => {
        const state = get().maps[mapId];
        return state?.completedItems.length ?? 0;
      },

      shouldShowPanel: (mapId: string): boolean => {
        const state = get().maps[mapId];
        if (!state) return true; // First visit, show panel
        if (state.dismissed) return false;
        if (state.completedItems.length >= TOTAL_ITEMS) return false;
        return true;
      },

      resetMap: (mapId: string) => {
        set((state) => ({
          maps: {
            ...state.maps,
            [mapId]: { ...DEFAULT_MAP_STATE },
          },
        }));
      },
    }),
    {
      name: "wondermaps:map-getting-started",
      version: 1,
    }
  )
);

// =============================================================================
// Selectors
// =============================================================================

export const GETTING_STARTED_ITEMS: {
  id: GettingStartedItem;
  label: string;
  activeLabel: string;
}[] = [
  {
    id: "poi_created",
    label: "Create a POI",
    activeLabel: "Create POI",
  },
  {
    id: "poi_enriched",
    label: "Enrich a POI",
    activeLabel: "Enrich POI",
  },
  {
    id: "label_added",
    label: "Add a label",
    activeLabel: "Add Label",
  },
  {
    id: "map_published",
    label: "Publish your map",
    activeLabel: "Publish",
  },
];
